import "./polyfills/crypto-randomuuid";

import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { StrictMode } from "react";
import { Async } from "react-async";
import * as ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router/dom";

import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { initializeCookieBot } from "@/external/cookiebot";
import { initializeGoogleAnalytics } from "@/external/g-analytics";
import { getPostHogConfig } from "@/external/posthog";
import { loadPostHog } from "@/external/posthog-client";
import { initializeSentryReplay } from "@/external/sentry";
import { registerChunkLoadRecovery } from "@/helpers/chunk-load-recovery";
import { isMockMode } from "@/helpers/local-mock";
import { ApplicationLoadingIndicator, StartupLoading } from "@/providers/StartupLoading";

import { createAppRouter } from "./App";
import { createQueryClient } from "./helpers/query-client";

const queryClient = createQueryClient();

// Local performance checks read the whole startup from Resource Timing; dev:mock alone loads
// thousands of modules, far beyond the default buffer of 250 entries.
if (isMockMode) {
  performance.setResourceTimingBufferSize(5000);
}

async function initialize() {
  // Cookie bot must go first to potentially block cookies
  await initializeCookieBot(() => {
    // Mock runs use fake data; keep them out of the production analytics property.
    if (!isMockMode) {
      initializeGoogleAnalytics();
    }
  });

  if (!("container" in document.documentElement.style)) {
    await import("container-query-polyfill");
  }

  if (import.meta.env.VITE_MOCK_API_IN_DEVELOPMENT === "true") {
    // imported dynamically to avoid being included in production
    console.warn("Mocks enabled");
    const { worker } = await import("./helpers/mocks/server");
    await worker.start({ onUnhandledRequest: "bypass", quiet: true });
  }
}

const initializePromise = initialize();
const router = createAppRouter({ queryClient, appReady: initializePromise });
registerChunkLoadRecovery(router);

const rootElement = document.getElementById("root")!;
const root = ReactDOM.createRoot(rootElement);

root.render(
  <StrictMode>
    <ApplicationLoadingIndicator />
    <Sentry.ErrorBoundary fallback={<ConnectionFailurePage />}>
      <Async promise={initializePromise}>
        <Async.Pending>
          <StartupLoading />
        </Async.Pending>
        <Async.Rejected>
          <ConnectionFailurePage />
        </Async.Rejected>
        <Async.Fulfilled>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            {import.meta.env.VITE_REACT_QUERY_DEVTOOLS === "true" && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
        </Async.Fulfilled>
      </Async>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

// Replay and analytics are optional. A failed load must not surface as an unhandled rejection.
initializeSentryReplay().catch(() => {});
// PostHog sets cookies, so it starts only after Cookiebot has run in initialize().
initializePromise.then(() => loadPostHog(getPostHogConfig())).catch(() => {});
